import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Checkbox,
  DateField,
  FormattingToolbar,
  IconButton,
  Markdown,
  Menu,
  MonoLabel,
  Prio,
  Row,
  Tabs,
  TextField
} from '../primitives';
import type { FormattingAction } from '../primitives/FormattingToolbar';
import type { PrioLevel } from '../primitives';
import type { PersonalTodosVMActions } from '../../lib/vm/usePersonalTodosVM';
import {
  bodyByteLength,
  formatRelativeDue,
  PERSONAL_TODOS_BODY_HARD_CAP_BYTES,
  PERSONAL_TODOS_BODY_SOFT_CAP_BYTES,
  type PersonalTodoPriority,
  type PersonalTodoRow
} from '../../lib/models/personalTodos';
import type { RowEditSessionView } from '../../hooks/usePersonalTodoEditSession';
import { useTextareaAutoGrow } from '../../hooks/useTextareaAutoGrow';
import {
  applyFormattingAction,
  detectActiveFormats,
  type ActiveFormats
} from '../../lib/markdownFormatting';

const PRIO_LEVEL_MAP: Record<PersonalTodoPriority, PrioLevel> = {
  high: 'high',
  medium: 'med',
  low: 'low'
};

const PRIO_LABEL_MAP: Record<PersonalTodoPriority, string> = {
  high: 'HIGH',
  medium: 'MED',
  low: 'LOW'
};

const NEXT_PRIO: Record<PersonalTodoPriority, PersonalTodoPriority> = {
  low: 'medium',
  medium: 'high',
  high: 'low'
};

type BodyTab = 'source' | 'preview';

export interface PersonalTodoRowProps {
  row: PersonalTodoRow;
  done: boolean;
  archived: boolean;
  showReorder: boolean;
  sortLocked: boolean;
  isFirst: boolean;
  isLast: boolean;
  editSession: RowEditSessionView | null;
  onTitleClick: () => void;
  onTitleFocus: () => void;
  onBodyFocus: () => void;
  onTitleDraftChange: (next: string) => void;
  onBodyDraftChange: (next: string) => void;
  onSave: () => void;
  onOpenNotes: () => void;
  onCloseNotes: () => void;
  actions: PersonalTodosVMActions;
}

export function PersonalTodoRow({
  row,
  done,
  archived,
  showReorder,
  sortLocked,
  isFirst,
  isLast,
  editSession,
  onTitleClick,
  onTitleFocus,
  onBodyFocus,
  onTitleDraftChange,
  onBodyDraftChange,
  onSave,
  onOpenNotes,
  onCloseNotes,
  actions
}: PersonalTodoRowProps) {
  const template = showReorder
    ? 'auto auto auto minmax(0,1fr) auto auto auto'
    : 'auto minmax(0,1fr) auto auto auto';

  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [bodyTab, setBodyTab] = useState<BodyTab>('source');
  const [activeFormats, setActiveFormats] = useState<ActiveFormats>({});
  const [editingDate, setEditingDate] = useState(false);
  const isOpen = editSession?.isOpen ?? false;
  const titleEditing = editSession?.titleEditing ?? false;
  const bodyDraft = editSession?.bodyDraft ?? row.body ?? '';
  const titleDraft = editSession?.titleDraft ?? row.title;
  const sourceActive = isOpen && bodyTab === 'source';

  useTextareaAutoGrow(bodyRef, bodyDraft, sourceActive);

  useEffect(() => {
    if (!isOpen) setBodyTab('source');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || titleEditing) return;
    bodyRef.current?.focus();
  }, [isOpen, titleEditing, row.id]);

  const reportActiveFormats = useCallback(() => {
    const ta = bodyRef.current;
    if (!ta || !sourceActive) return;
    setActiveFormats(
      detectActiveFormats(bodyDraft, ta.selectionStart, ta.selectionEnd)
    );
  }, [bodyDraft, sourceActive]);

  const runSyntax = useCallback(
    (action: FormattingAction) => {
      const ta = bodyRef.current;
      if (!ta || !sourceActive) return;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const result = applyFormattingAction(bodyDraft, start, end, action);
      onBodyDraftChange(result.value);
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(result.selectionStart, result.selectionEnd);
        setActiveFormats(
          detectActiveFormats(
            result.value,
            result.selectionStart,
            result.selectionEnd
          )
        );
      });
    },
    [bodyDraft, onBodyDraftChange, sourceActive]
  );

  const bodyBytes = bodyByteLength(bodyDraft);
  const bodySizeClass =
    bodyBytes >= PERSONAL_TODOS_BODY_HARD_CAP_BYTES
      ? 'error'
      : bodyBytes >= PERSONAL_TODOS_BODY_SOFT_CAP_BYTES
        ? 'warn'
        : '';

  const dueDisplay = formatRelativeDue(row.due_date);
  const dueOverdue =
    row.due_date != null && dueDisplay.includes('overdue');

  const copyLink = () => {
    const url = `${window.location.origin}/personal-todos?focus=${row.id}`;
    void navigator.clipboard?.writeText(url);
  };

  const menuItems = archived
    ? [
        {
          id: 'restore',
          label: 'Restore',
          onSelect: () => void actions.restore(row.id),
          'data-testid': `pt-row-restore-${row.id}`
        }
      ]
    : [
        {
          id: isOpen ? 'close-notes' : 'open-notes',
          label: isOpen ? 'Close notes' : 'Open notes',
          onSelect: isOpen ? onCloseNotes : onOpenNotes,
          'data-testid': isOpen
            ? `pt-row-close-notes-${row.id}`
            : `pt-row-open-notes-${row.id}`
        },
        {
          id: 'copy-link',
          label: 'Copy link',
          onSelect: copyLink,
          'data-testid': `pt-row-copy-link-${row.id}`
        },
        {
          id: 'archive',
          label: 'Archive',
          onSelect: () => void actions.archive(row.id),
          'data-testid': `pt-row-archive-${row.id}`
        }
      ];

  return (
    <div
      className={['pt-row-wrap', isOpen ? 'pt-row-wrap-expanded' : '']
        .filter(Boolean)
        .join(' ')}
      data-testid={`pt-row-${row.id}`}
      data-status={row.status}
      data-edit-open={isOpen ? '1' : '0'}
    >
      <Row template={template}>
        <Checkbox
          label={<span className="pn-field-label-hidden">Toggle done</span>}
          checked={done}
          disabled={archived}
          onChange={() => {
            if (!archived) void actions.toggleDone(row.id);
          }}
          aria-label={`Mark "${row.title}" done`}
          data-testid={`pt-row-checkbox-${row.id}`}
        />
        {showReorder ? (
          <>
            <IconButton
              size={24}
              aria-label="Move up"
              disabled={sortLocked || isFirst}
              onClick={() => void actions.moveUp(row.id)}
              data-testid={`pt-row-up-${row.id}`}
            >
              ↑
            </IconButton>
            <IconButton
              size={24}
              aria-label="Move down"
              disabled={sortLocked || isLast}
              onClick={() => void actions.moveDown(row.id)}
              data-testid={`pt-row-down-${row.id}`}
            >
              ↓
            </IconButton>
          </>
        ) : null}
        {titleEditing && !archived ? (
          <TextField
            id={`pt-row-title-input-${row.id}`}
            className="pt-row-title-field"
            label={<span className="pn-field-label-hidden">Title</span>}
            hideLabel
            inline
            value={titleDraft}
            onChange={(e) => onTitleDraftChange(e.currentTarget.value)}
            onFocus={onTitleFocus}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void onSave();
              }
            }}
            data-testid={`pt-row-title-input-${row.id}`}
            autoFocus
          />
        ) : (
          <button
            type="button"
            className={[
              'pt-row-title-btn',
              done ? 'pt-row-title-done' : '',
              isOpen ? 'pt-row-title-btn-active' : ''
            ]
              .filter(Boolean)
              .join(' ')}
            disabled={archived}
            onClick={() => {
              if (!archived) onTitleClick();
            }}
            aria-expanded={isOpen}
            data-testid={`pt-row-title-${row.id}`}
          >
            {isOpen ? titleDraft : row.title}
          </button>
        )}
        {!archived ? (
          <button
            type="button"
            className="pt-prio-btn"
            aria-label={`Cycle priority for "${row.title}" (currently ${row.priority})`}
            onClick={() =>
              void actions.setPriority(row.id, NEXT_PRIO[row.priority])
            }
            data-testid={`pt-row-prio-${row.id}`}
          >
            <Prio level={PRIO_LEVEL_MAP[row.priority]}>
              {PRIO_LABEL_MAP[row.priority]}
            </Prio>
          </button>
        ) : (
          <Prio level={PRIO_LEVEL_MAP[row.priority]}>
            {PRIO_LABEL_MAP[row.priority]}
          </Prio>
        )}
        {!archived && editingDate ? (
          <DateField
            id={`pt-row-due-input-${row.id}`}
            label={<span className="pn-field-label-hidden">Due date</span>}
            value={row.due_date}
            hideLabel
            inline
            autoFocus
            testId={`pt-row-due-input-${row.id}`}
            onChange={(next) => {
              void actions.setDueDate(row.id, next);
              setEditingDate(false);
            }}
            onBlur={() => setEditingDate(false)}
          />
        ) : (
          <button
            type="button"
            className={['pt-due-btn', dueOverdue ? 'pt-due-overdue' : '']
              .filter(Boolean)
              .join(' ')}
            disabled={archived}
            aria-label={`Edit due date for "${row.title}"`}
            onClick={() => {
              if (!archived) setEditingDate(true);
            }}
            data-testid={`pt-row-due-${row.id}`}
          >
            <MonoLabel size="sm">{dueDisplay}</MonoLabel>
          </button>
        )}
        <Menu
          label="Row actions"
          align="end"
          data-testid={`pt-row-menu-${row.id}`}
          trigger={(api) => (
            <IconButton
              size={24}
              aria-label="Row actions"
              data-testid={`pt-row-menu-trigger-${row.id}`}
              {...api}
            >
              ⋮
            </IconButton>
          )}
          items={menuItems}
        />
      </Row>

      {isOpen ? (
        <div className="pt-row-body" data-testid={`pt-row-body-${row.id}`}>
          <Tabs
            items={[
              { value: 'source', label: 'Source' },
              { value: 'preview', label: 'Preview' }
            ]}
            value={bodyTab}
            onChange={(v) => setBodyTab(v as BodyTab)}
            label="Body editor mode"
            dataTestId={`pt-row-body-tabs-${row.id}`}
          />
          {bodyTab === 'source' ? (
            <textarea
              ref={bodyRef}
              className="pt-row-body-textarea"
              value={bodyDraft}
              placeholder="Markdown notes…"
              onChange={(e) => onBodyDraftChange(e.target.value)}
              onFocus={onBodyFocus}
              onSelect={reportActiveFormats}
              onKeyUp={reportActiveFormats}
              onMouseUp={reportActiveFormats}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  void onSave();
                }
              }}
              data-testid={`pt-row-body-textarea-${row.id}`}
            />
          ) : (
            <Markdown className="pt-row-body-preview">
              {bodyDraft || '_No notes yet._'}
            </Markdown>
          )}
          <FormattingToolbar
            visible={sourceActive}
            onInsert={runSyntax}
            activeActions={activeFormats}
            className="pt-fmt-float-toolbar"
            dataTestId={`pt-row-format-toolbar-${row.id}`}
          />
          <div className="pt-row-body-footer">
            <span className={['pt-row-body-size', bodySizeClass].join(' ')}>
              {(bodyBytes / 1024).toFixed(1)} KiB / 16 KiB
            </span>
            {editSession?.footerLabel ? (
              <span
                className={[
                  'pt-row-body-save',
                  editSession.footerLabel.startsWith('Saved') ? 'saved' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
                data-testid={`pt-row-body-save-${row.id}`}
              >
                {editSession.footerLabel}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
